// src/observable.ts
var Observable = class _Observable {
  constructor(teardown) {
    this._subscribers = /* @__PURE__ */ new Set();
    this._hasValue = false;
    this._disposed = false;
    this._teardown = teardown;
  }
  /** Emit a value to all subscribers */
  next(value) {
    if (this._disposed) return;
    this._lastValue = value;
    this._hasValue = true;
    const subs = [...this._subscribers];
    for (const sub of subs) {
      try {
        sub(value);
      } catch (err) {
        console.error("[DryStore Observable] Subscriber threw:", err);
      }
    }
  }
  /** Subscribe to value changes. Returns unsubscribe function. */
  subscribe(fn) {
    if (this._disposed) {
      return () => {
      };
    }
    this._subscribers.add(fn);
    if (this._hasValue) {
      try {
        fn(this._lastValue);
      } catch (err) {
        console.error("[DryStore Observable] Subscriber threw on replay:", err);
      }
    }
    return () => {
      this._subscribers.delete(fn);
      if (this._subscribers.size === 0 && this._teardown) {
        this._teardown();
      }
    };
  }
  /** Get current value synchronously (undefined if no value emitted) */
  getValue() {
    return this._lastValue;
  }
  get hasValue() {
    return this._hasValue;
  }
  get subscriberCount() {
    return this._subscribers.size;
  }
  get isDisposed() {
    return this._disposed;
  }
  /** Transform emitted values. Unsubscribes from source when derived is disposed. */
  map(fn) {
    const unsub = { current: null };
    const mapped = new _Observable(() => {
      unsub.current?.();
    });
    unsub.current = this.subscribe((value) => mapped.next(fn(value)));
    return mapped;
  }
  /** Filter emitted values. Unsubscribes from source when derived is disposed. */
  filter(fn) {
    const unsub = { current: null };
    const filtered = new _Observable(() => {
      unsub.current?.();
    });
    unsub.current = this.subscribe((value) => {
      if (fn(value)) filtered.next(value);
    });
    return filtered;
  }
  /** Dispose all subscribers and run teardown */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this._subscribers.clear();
    this._teardown?.();
  }
};
function combineLatest(sources) {
  const unsubs = [];
  const combined = new Observable(() => {
    for (const unsub of unsubs) unsub();
  });
  const values = new Array(sources.length);
  const hasValue = new Array(sources.length).fill(false);
  sources.forEach((source, i) => {
    unsubs.push(
      source.subscribe((value) => {
        values[i] = value;
        hasValue[i] = true;
        if (hasValue.every(Boolean)) {
          combined.next([...values]);
        }
      })
    );
  });
  return combined;
}

// src/schema.ts
var ValidationError = class extends Error {
  constructor(message, field, value) {
    super(message);
    this.field = field;
    this.value = value;
    this.name = "ValidationError";
  }
};
function validateDoc(doc, schema) {
  if (typeof doc !== "object" || doc === null || Array.isArray(doc)) {
    throw new ValidationError("Document must be a plain object", "", doc);
  }
  const pk = schema.primaryKey;
  if (doc[pk] === void 0 || doc[pk] === null) {
    throw new ValidationError(`Primary key "${pk}" is required`, pk, doc[pk]);
  }
  if (schema.required) {
    for (const field of schema.required) {
      if (doc[field] === void 0 || doc[field] === null) {
        throw new ValidationError(`Required field "${field}" is missing`, field, doc[field]);
      }
    }
  }
  for (const [key, value] of Object.entries(doc)) {
    const propSchema = schema.properties[key];
    if (propSchema) {
      validateProperty(key, value, propSchema);
    }
  }
}
function validateProperty(field, value, prop) {
  if (value === void 0 || value === null) return;
  const actualType = Array.isArray(value) ? "array" : typeof value;
  if (actualType !== prop.type) {
    throw new ValidationError(
      `Field "${field}" expected type "${prop.type}", got "${actualType}"`,
      field,
      value
    );
  }
  if (prop.type === "string" && typeof value === "string") {
    if (prop.maxLength !== void 0 && value.length > prop.maxLength) {
      throw new ValidationError(
        `Field "${field}" exceeds maxLength of ${prop.maxLength}`,
        field,
        value
      );
    }
    if (prop.enum && !prop.enum.includes(value)) {
      throw new ValidationError(
        `Field "${field}" must be one of: ${prop.enum.join(", ")}`,
        field,
        value
      );
    }
  }
  if (prop.type === "number" && typeof value === "number") {
    if (prop.minimum !== void 0 && value < prop.minimum) {
      throw new ValidationError(
        `Field "${field}" must be >= ${prop.minimum}`,
        field,
        value
      );
    }
    if (prop.maximum !== void 0 && value > prop.maximum) {
      throw new ValidationError(
        `Field "${field}" must be <= ${prop.maximum}`,
        field,
        value
      );
    }
  }
  if (prop.type === "array" && Array.isArray(value) && prop.items) {
    value.forEach((item, i) => validateProperty(`${field}[${i}]`, item, prop.items));
  }
  if (prop.type === "object" && typeof value === "object" && prop.properties) {
    for (const [k, v] of Object.entries(value)) {
      if (prop.properties[k]) {
        validateProperty(`${field}.${k}`, v, prop.properties[k]);
      }
    }
    if (prop.required) {
      for (const req of prop.required) {
        if (value[req] === void 0) {
          throw new ValidationError(`Field "${field}.${req}" is required`, `${field}.${req}`, void 0);
        }
      }
    }
  }
}

// src/query.ts
var MAX_REGEX_LENGTH = 500;
var REDOS_PATTERNS = [
  /(\(.+[\*\+]\)[\*\+\?])/,
  // (a+)+, (a*)*
  /(\(.+\)\{.+\}[\*\+\?])/,
  // (a){2,}+
  /\([^)]*\|[^)]*\)[\*\+]/
  // (a|a)*, (a|ab)*
];
function validateRegexPattern(pattern) {
  if (pattern.length > MAX_REGEX_LENGTH) {
    throw new Error(`Regex pattern exceeds max length (${MAX_REGEX_LENGTH})`);
  }
  for (const redos of REDOS_PATTERNS) {
    if (redos.test(pattern)) {
      throw new Error(`Regex pattern rejected: potential ReDoS (catastrophic backtracking)`);
    }
  }
}
var regexCache = /* @__PURE__ */ new Map();
function getCachedRegex(pattern) {
  const existing = regexCache.get(pattern);
  if (existing) {
    regexCache.delete(pattern);
    regexCache.set(pattern, existing);
    return existing;
  }
  validateRegexPattern(pattern);
  const re = new RegExp(pattern);
  if (regexCache.size >= 100) {
    const oldest = regexCache.keys().next().value;
    regexCache.delete(oldest);
  }
  regexCache.set(pattern, re);
  return re;
}
function matchesSelector(doc, selector) {
  for (const [key, condition] of Object.entries(selector)) {
    const value = doc[key];
    if (condition === null || condition === void 0) {
      if (value !== condition) return false;
      continue;
    }
    if (typeof condition === "object" && condition !== null && !Array.isArray(condition)) {
      const ops = condition;
      if ("$eq" in ops && value !== ops.$eq) return false;
      if ("$ne" in ops && value === ops.$ne) return false;
      if ("$gt" in ops && !(typeof value === "number" && value > ops.$gt)) return false;
      if ("$gte" in ops && !(typeof value === "number" && value >= ops.$gte)) return false;
      if ("$lt" in ops && !(typeof value === "number" && value < ops.$lt)) return false;
      if ("$lte" in ops && !(typeof value === "number" && value <= ops.$lte)) return false;
      if ("$in" in ops && !ops.$in.includes(value)) return false;
      if ("$nin" in ops && ops.$nin.includes(value)) return false;
      if ("$regex" in ops) {
        if (typeof value !== "string") return false;
        if (!getCachedRegex(ops.$regex).test(value)) return false;
      }
    } else {
      if (value !== condition) return false;
    }
  }
  return true;
}
function executeQuery(docs, query) {
  let results = docs;
  if (query.selector) {
    results = results.filter((doc) => matchesSelector(doc, query.selector));
  }
  if (query.sort && query.sort.length > 0) {
    results = [...results].sort((a, b) => {
      for (const sortObj of query.sort) {
        for (const [key, direction] of Object.entries(sortObj)) {
          const aVal = a[key];
          const bVal = b[key];
          if (aVal === bVal) continue;
          const cmp = aVal < bVal ? -1 : 1;
          return direction === "desc" ? -cmp : cmp;
        }
      }
      return 0;
    });
  }
  if (query.skip) {
    results = results.slice(query.skip);
  }
  if (query.limit) {
    results = results.slice(0, query.limit);
  }
  return results;
}

// src/collection.ts
function changeAffectsQuery(event, query) {
  if (!query.selector) return true;
  if (event.operation === "insert" || event.operation === "update") {
    if (matchesSelector(event.doc, query.selector)) return true;
    if (event.operation === "update" && event.previousDoc) {
      if (matchesSelector(event.previousDoc, query.selector)) return true;
    }
    return false;
  }
  if (event.operation === "delete") {
    return matchesSelector(event.doc, query.selector);
  }
  return true;
}
var RxQuery = class {
  constructor(collection, query) {
    this._collection = collection;
    this._query = query;
  }
  /** Execute the query once */
  async exec() {
    return this._collection.storage.find(this._query);
  }
  /**
   * Subscribe to live query results — re-emits on every relevant change.
   * Smart invalidation: only re-queries when the change could affect results.
   */
  get $() {
    let changesUnsub = null;
    let batchUnsub = null;
    const queryOpts = this._query;
    const execFn = () => this.exec();
    const obs = new Observable(() => {
      if (changesUnsub) {
        changesUnsub();
        changesUnsub = null;
      }
      if (batchUnsub) {
        batchUnsub();
        batchUnsub = null;
      }
    });
    let pending = false;
    const scheduleRequery = () => {
      if (pending) return;
      pending = true;
      Promise.resolve().then(() => {
        pending = false;
        execFn().then(
          (docs) => obs.next(docs),
          (err) => console.error("[DryStore RxQuery] Reactive query failed:", err)
        );
      });
    };
    execFn().then(
      (docs) => obs.next(docs),
      (err) => console.error("[DryStore RxQuery] Initial query failed:", err)
    );
    changesUnsub = this._collection.changes$.subscribe((event) => {
      if (changeAffectsQuery(event, queryOpts)) {
        scheduleRequery();
      }
    });
    batchUnsub = this._collection.batchChanges$.subscribe(() => {
      scheduleRequery();
    });
    return obs;
  }
};
var AsyncMutex = class {
  constructor() {
    this._queue = [];
    this._locked = false;
  }
  async acquire() {
    if (!this._locked) {
      this._locked = true;
      return;
    }
    return new Promise((resolve) => {
      this._queue.push(resolve);
    });
  }
  release() {
    const next = this._queue.shift();
    if (next) {
      next();
    } else {
      this._locked = false;
    }
  }
};
var Collection = class {
  constructor(name, config) {
    this.changes$ = new Observable();
    /** Emitted once per bulk operation with all changes */
    this.batchChanges$ = new Observable();
    this._mutex = new AsyncMutex();
    this.name = name;
    this._schema = config.schema;
    this.storage = config.storage;
    this.primaryKey = config.schema.primaryKey;
  }
  get schema() {
    return this._schema;
  }
  _getKey(doc) {
    return String(doc[this.primaryKey]);
  }
  /** Insert a new document */
  async insert(doc) {
    validateDoc(doc, this._schema);
    await this._mutex.acquire();
    try {
      await this.storage.write({ insert: [doc] });
      this.changes$.next({
        operation: "insert",
        doc,
        timestamp: Date.now()
      });
      return doc;
    } finally {
      this._mutex.release();
    }
  }
  /** Insert multiple documents — emits ONE batch event instead of N individual events */
  async bulkInsert(docs) {
    for (const doc of docs) {
      validateDoc(doc, this._schema);
    }
    await this._mutex.acquire();
    try {
      await this.storage.write({ insert: docs });
      const now = Date.now();
      const operations = docs.map((doc) => ({
        operation: "insert",
        doc,
        timestamp: now
      }));
      this.batchChanges$.next({ operations, timestamp: now });
      for (const op of operations) {
        this.changes$.next(op);
      }
      return docs;
    } finally {
      this._mutex.release();
    }
  }
  /** Update an existing document (full replacement) */
  async update(doc) {
    validateDoc(doc, this._schema);
    const key = this._getKey(doc);
    await this._mutex.acquire();
    try {
      const previous = await this.storage.get(key);
      if (!previous) {
        throw new Error(`Document with ${this.primaryKey}="${key}" not found`);
      }
      await this.storage.write({ update: [doc] });
      this.changes$.next({
        operation: "update",
        doc,
        previousDoc: previous,
        timestamp: Date.now()
      });
      return doc;
    } finally {
      this._mutex.release();
    }
  }
  /** Insert or update a document (atomic check-then-act) */
  async upsert(doc) {
    validateDoc(doc, this._schema);
    const key = this._getKey(doc);
    await this._mutex.acquire();
    try {
      const existing = await this.storage.get(key);
      if (existing) {
        await this.storage.write({ update: [doc] });
        this.changes$.next({
          operation: "update",
          doc,
          previousDoc: existing,
          timestamp: Date.now()
        });
      } else {
        await this.storage.write({ insert: [doc] });
        this.changes$.next({
          operation: "insert",
          doc,
          timestamp: Date.now()
        });
      }
      return doc;
    } finally {
      this._mutex.release();
    }
  }
  /** Delete a document by primary key */
  async delete(key) {
    await this._mutex.acquire();
    try {
      const doc = await this.storage.get(key);
      if (!doc) return false;
      await this.storage.write({ delete: [key] });
      this.changes$.next({
        operation: "delete",
        doc,
        timestamp: Date.now()
      });
      return true;
    } finally {
      this._mutex.release();
    }
  }
  /** Get a single document by primary key */
  async findOne(key) {
    return this.storage.get(key);
  }
  /** Create a query builder */
  find(query) {
    return new RxQuery(this, query ?? {});
  }
  /** Count documents */
  async count(selector) {
    return this.storage.count(selector);
  }
  /** Remove all documents from the collection */
  async clear() {
    await this._mutex.acquire();
    try {
      const all = await this.storage.getAll();
      if (all.length === 0) return;
      const keys = all.map((doc) => String(doc[this.primaryKey]));
      await this.storage.write({ delete: keys });
      const now = Date.now();
      this.batchChanges$.next({
        operations: all.map((doc) => ({ operation: "delete", doc, timestamp: now })),
        timestamp: now
      });
      if (all.length > 0) {
        this.changes$.next({ operation: "delete", doc: all[all.length - 1], timestamp: now });
      }
    } finally {
      this._mutex.release();
    }
  }
  /** Dispose collection resources */
  async close() {
    this.changes$.dispose();
    this.batchChanges$.dispose();
    await this.storage.close();
  }
};

// src/database.ts
var Database = class {
  constructor(config) {
    this._collections = /* @__PURE__ */ new Map();
    this._closed = false;
    this._config = config;
  }
  get name() {
    return this._config.name;
  }
  /** Add collections to the database */
  async addCollections(configs) {
    if (this._closed) throw new Error("Database is closed");
    const result = {};
    for (const [name, config] of Object.entries(configs)) {
      if (this._collections.has(name)) {
        throw new Error(`Collection "${name}" already exists`);
      }
      const storage = this._config.storage(name, config.schema.primaryKey);
      const collection = new Collection(name, {
        schema: config.schema,
        storage
      });
      this._collections.set(name, collection);
      result[name] = collection;
    }
    return result;
  }
  /** Get a collection by name */
  collection(name) {
    return this._collections.get(name);
  }
  /** List all collection names */
  get collectionNames() {
    return Array.from(this._collections.keys());
  }
  /** Clear all documents from all collections */
  async clearAll() {
    if (this._closed) throw new Error("Database is closed");
    for (const collection of this._collections.values()) {
      await collection.clear();
    }
  }
  /** Close all collections and the database */
  async close() {
    this._closed = true;
    for (const collection of this._collections.values()) {
      await collection.close();
    }
    this._collections.clear();
  }
};
function createDatabase(config) {
  return new Database(config);
}

// src/errors.ts
var DryError = class extends Error {
  constructor(code, message, context = {}) {
    super(message);
    this.name = "DryError";
    this.code = code;
    this.context = context;
  }
  get collectionName() {
    return this.context.collection;
  }
  get documentId() {
    return this.context.documentId;
  }
  get suggestions() {
    return this.context.suggestions ?? [];
  }
  toJSON() {
    return {
      code: this.code,
      message: this.message,
      context: this.context
    };
  }
};
function notFound(collection, key, operation = "update") {
  return new DryError(
    "DOCUMENT_NOT_FOUND",
    `[${collection}] Cannot ${operation}: no document with id="${key}"`,
    {
      collection,
      operation,
      documentId: key,
      suggestions: [
        "The document may have been deleted by another client",
        "Use collection.findOne() to check if the document exists",
        "Use collection.upsert() to insert-or-update safely"
      ]
    }
  );
}
function duplicateKey(collection, key) {
  return new DryError(
    "DUPLICATE_KEY",
    `[${collection}] Document with id="${key}" already exists`,
    {
      collection,
      operation: "insert",
      documentId: key,
      suggestions: [
        "Use collection.upsert() to insert-or-update",
        "Generate a unique ID with crypto.randomUUID()"
      ]
    }
  );
}

// src/schema-builder.ts
function makeField(type, opts) {
  return {
    _type: type,
    _tsType: null,
    _schemaProperty: { type, ...opts }
  };
}
var t = {
  string: (opts) => makeField("string", opts),
  number: (opts) => makeField("number", opts),
  boolean: () => makeField("boolean"),
  /** Enum field — restricted to specific string values */
  enum: (values) => makeField("string", { enum: values }),
  array: (items) => makeField("array", { items: items._schemaProperty }),
  object: (properties, opts) => {
    const schemaProps = {};
    for (const [k, v] of Object.entries(properties)) {
      schemaProps[k] = v._schemaProperty;
    }
    return makeField("object", {
      properties: schemaProps,
      required: opts?.required
    });
  }
};
function defineSchema(version, fields, options) {
  const properties = {};
  for (const [k, v] of Object.entries(fields)) {
    properties[k] = v._schemaProperty;
  }
  const schema = {
    version,
    primaryKey: options.primaryKey,
    type: "object",
    properties,
    required: options.required,
    indexes: options.indexes
  };
  return {
    _fields: fields,
    schema,
    version
  };
}

// src/pagination.ts
function encodeCursor(sortValue, primaryKey) {
  return btoa(JSON.stringify({ s: sortValue, k: primaryKey }));
}
function decodeCursor(cursor) {
  const { s, k } = JSON.parse(atob(cursor));
  return { sortValue: s, primaryKey: k };
}
async function paginatedQuery(find, query, pagination, primaryKeyField, sortField) {
  const sort = query.sort?.[0];
  const field = sortField ?? (sort ? Object.keys(sort)[0] : primaryKeyField);
  const direction = sort ? Object.values(sort)[0] : "asc";
  const selector = { ...query.selector };
  if (pagination.after) {
    const { sortValue } = decodeCursor(pagination.after);
    if (direction === "asc") {
      selector[field] = { $gte: sortValue };
    } else {
      selector[field] = { $lte: sortValue };
    }
  }
  const docs = await find({
    ...query,
    selector,
    limit: pagination.pageSize + 1 + (pagination.after ? 1 : 0),
    skip: void 0
  }).exec();
  let results = docs;
  if (pagination.after) {
    const { primaryKey: cursorPk } = decodeCursor(pagination.after);
    const cursorIdx = results.findIndex(
      (d) => String(d[primaryKeyField]) === cursorPk
    );
    if (cursorIdx !== -1) {
      results = results.slice(cursorIdx + 1);
    }
  }
  const hasMore = results.length > pagination.pageSize;
  const page = results.slice(0, pagination.pageSize);
  const lastDoc = page[page.length - 1];
  const cursor = lastDoc ? encodeCursor(lastDoc[field], String(lastDoc[primaryKeyField])) : null;
  return { docs: page, cursor, hasMore };
}

// src/hooks.ts
function withTimestamps() {
  return {
    beforeInsert: (doc) => ({
      ...doc,
      _createdAt: doc._createdAt ?? Date.now(),
      _updatedAt: Date.now()
    }),
    beforeUpdate: (newDoc) => ({
      ...newDoc,
      _updatedAt: Date.now()
    })
  };
}
function withSoftDelete(collection) {
  return {
    beforeDelete: async (doc) => {
      await collection.update({
        ...doc,
        _deleted: true,
        _deletedAt: Date.now()
      });
      return false;
    }
  };
}
function mergeHooks(...hookSets) {
  const merged = {};
  merged.beforeInsert = async (doc) => {
    let result = doc;
    for (const hooks of hookSets) {
      if (hooks.beforeInsert) result = await hooks.beforeInsert(result);
    }
    return result;
  };
  merged.afterInsert = async (doc) => {
    for (const hooks of hookSets) {
      if (hooks.afterInsert) await hooks.afterInsert(doc);
    }
  };
  merged.beforeUpdate = async (newDoc, oldDoc) => {
    let result = newDoc;
    for (const hooks of hookSets) {
      if (hooks.beforeUpdate) result = await hooks.beforeUpdate(result, oldDoc);
    }
    return result;
  };
  merged.afterUpdate = async (newDoc, oldDoc) => {
    for (const hooks of hookSets) {
      if (hooks.afterUpdate) await hooks.afterUpdate(newDoc, oldDoc);
    }
  };
  merged.beforeDelete = async (doc) => {
    for (const hooks of hookSets) {
      if (hooks.beforeDelete) {
        const proceed = await hooks.beforeDelete(doc);
        if (!proceed) return false;
      }
    }
    return true;
  };
  merged.afterDelete = async (doc) => {
    for (const hooks of hookSets) {
      if (hooks.afterDelete) await hooks.afterDelete(doc);
    }
  };
  return merged;
}

// src/soft-delete.ts
var DEFAULT_DELETED_FIELD = "_deleted";
var DEFAULT_TIMESTAMP_FIELD = "_deletedAt";
var SoftDeleteStorage = class {
  constructor(inner, primaryKey, config = {}) {
    this._inner = inner;
    this._primaryKey = primaryKey;
    this._config = config;
    this.name = `soft-delete(${inner.name})`;
    this._deletedField = config.deletedField ?? DEFAULT_DELETED_FIELD;
    this._timestampField = config.timestampField ?? DEFAULT_TIMESTAMP_FIELD;
    this._purgeAfterMs = config.purgeAfterMs ?? 0;
  }
  async get(key) {
    const doc = await this._inner.get(key);
    if (doc && doc[this._deletedField]) return void 0;
    return doc;
  }
  async getMany(keys) {
    const docs = await this._inner.getMany(keys);
    return docs.filter((d) => !d[this._deletedField]);
  }
  async getAll() {
    const docs = await this._inner.getAll();
    return docs.filter((d) => !d[this._deletedField]);
  }
  async write(ops) {
    const modified = { ...ops };
    if (ops.delete && ops.delete.length > 0) {
      const softDeleted = [];
      for (const key of ops.delete) {
        const doc = await this._inner.get(key);
        if (doc) {
          softDeleted.push({
            ...doc,
            [this._deletedField]: true,
            [this._timestampField]: Date.now()
          });
        }
      }
      modified.delete = void 0;
      modified.update = [...modified.update ?? [], ...softDeleted];
    }
    return this._inner.write(modified);
  }
  async find(query) {
    const enhancedQuery = {
      ...query,
      selector: {
        ...query.selector,
        [this._deletedField]: { $ne: true }
      }
    };
    return this._inner.find(enhancedQuery);
  }
  async count(selector) {
    const enhanced = {
      ...selector,
      [this._deletedField]: { $ne: true }
    };
    return this._inner.count(enhanced);
  }
  async close() {
    return this._inner.close();
  }
  // ─── Soft delete specific methods ──────────────
  /** Recover a soft-deleted document */
  async recover(key) {
    const doc = await this._inner.get(key);
    if (!doc || !doc[this._deletedField]) return void 0;
    const recovered = { ...doc };
    delete recovered[this._deletedField];
    delete recovered[this._timestampField];
    await this._inner.write({ update: [recovered] });
    return recovered;
  }
  /** Get soft-deleted documents */
  async getDeleted() {
    const all = await this._inner.getAll();
    return all.filter((d) => d[this._deletedField] === true);
  }
  /** Permanently remove soft-deleted documents older than purgeAfterMs */
  async purge(maxAgeMs) {
    const age = maxAgeMs ?? this._purgeAfterMs;
    if (age <= 0) return 0;
    const deleted = await this.getDeleted();
    const now = Date.now();
    const toPurge = [];
    for (const doc of deleted) {
      const deletedAt = doc[this._timestampField];
      if (deletedAt && now - deletedAt > age) {
        toPurge.push(String(doc[this._primaryKey]));
      }
    }
    if (toPurge.length > 0) {
      await this._inner.write({ delete: toPurge });
    }
    return toPurge.length;
  }
  /**
   * Find including soft-deleted docs (for admin/audit views)
   */
  async findIncludingDeleted(query) {
    return this._inner.find(query);
  }
};
function createSoftDeleteStorage(inner, primaryKey, config) {
  return new SoftDeleteStorage(inner, primaryKey, config);
}

// src/audit.ts
var AuditLog = class {
  constructor(collection, primaryKey, changes$, config = {}) {
    this._entries = [];
    this._unsub = null;
    this._idCounter = 0;
    this._collection = collection;
    this._primaryKey = primaryKey;
    this._config = config;
    this._unsub = changes$.subscribe((event) => {
      this._record(event);
    });
  }
  /** Get all audit entries for a specific document */
  getHistory(documentId) {
    return this._entries.filter((e) => e.documentId === documentId);
  }
  /** Get all audit entries, optionally filtered */
  getAll(filter) {
    let results = this._entries;
    if (filter?.operation) results = results.filter((e) => e.operation === filter.operation);
    if (filter?.since) results = results.filter((e) => e.timestamp >= filter.since);
    if (filter?.limit) results = results.slice(-filter.limit);
    return results;
  }
  /** Get the most recent entry for a document */
  getLastChange(documentId) {
    for (let i = this._entries.length - 1; i >= 0; i--) {
      if (this._entries[i].documentId === documentId) return this._entries[i];
    }
    return void 0;
  }
  /** Total number of audit entries */
  get size() {
    return this._entries.length;
  }
  /** Purge old entries based on config */
  purge() {
    const maxAge = this._config.maxAgeMs ?? 90 * 24 * 60 * 60 * 1e3;
    if (maxAge <= 0) return 0;
    const cutoff = Date.now() - maxAge;
    const before = this._entries.length;
    this._entries = this._entries.filter((e) => e.timestamp >= cutoff);
    return before - this._entries.length;
  }
  /** Clear all entries */
  clear() {
    this._entries = [];
  }
  /** Stop recording */
  dispose() {
    if (this._unsub) {
      this._unsub();
      this._unsub = null;
    }
  }
  _record(event) {
    const documentId = String(event.doc[this._primaryKey]);
    const excludeFields = new Set(this._config.excludeFields ?? []);
    const sanitize = (doc) => {
      if (excludeFields.size === 0) return doc;
      const clean = { ...doc };
      for (const field of excludeFields) {
        delete clean[field];
      }
      return clean;
    };
    const entry = {
      id: `audit_${Date.now()}_${++this._idCounter}`,
      collection: this._collection,
      documentId,
      operation: event.operation,
      timestamp: event.timestamp
    };
    if (event.operation === "insert") {
      entry.after = sanitize(event.doc);
    } else if (event.operation === "update") {
      entry.before = event.previousDoc ? sanitize(event.previousDoc) : void 0;
      entry.after = sanitize(event.doc);
      if (event.previousDoc) {
        entry.changedFields = this._diffFields(event.previousDoc, event.doc, excludeFields);
      }
    } else if (event.operation === "delete") {
      entry.before = sanitize(event.doc);
    }
    this._entries.push(entry);
    const maxPerDoc = this._config.maxEntriesPerDoc ?? 100;
    if (maxPerDoc > 0) {
      const docEntries = this._entries.filter((e) => e.documentId === documentId);
      if (docEntries.length > maxPerDoc) {
        const toRemove = docEntries.length - maxPerDoc;
        let removed = 0;
        this._entries = this._entries.filter((e) => {
          if (e.documentId === documentId && removed < toRemove) {
            removed++;
            return false;
          }
          return true;
        });
      }
    }
  }
  _diffFields(before, after, excludeFields) {
    const changed = [];
    const allKeys = /* @__PURE__ */ new Set([...Object.keys(before), ...Object.keys(after)]);
    for (const key of allKeys) {
      if (excludeFields.has(key)) continue;
      const a = before[key];
      const b = after[key];
      if (JSON.stringify(a) !== JSON.stringify(b)) {
        changed.push(key);
      }
    }
    return changed;
  }
};

// src/id-remap.ts
var TEMP_PREFIX = "_temp_";
var MAX_MAPPINGS = 1e4;
function createIdRemapper(config) {
  const mappings = /* @__PURE__ */ new Map();
  let remapChain = Promise.resolve();
  return {
    async remap(collectionName, idMappings) {
      const col = config.collections[collectionName];
      if (!col) throw new Error(`Collection "${collectionName}" not found in remapper config`);
      const run = async () => {
        for (const mapping of idMappings) {
          mappings.set(mapping.tempId, mapping.realId);
          if (mappings.size > MAX_MAPPINGS) {
            const oldest = mappings.keys().next().value;
            mappings.delete(oldest);
          }
          await col.delete(mapping.tempId);
          if (mapping.doc) {
            await col.upsert(mapping.doc);
          }
          if (config.foreignKeys) {
            for (const [fkCollName, fkDefs] of Object.entries(config.foreignKeys)) {
              for (const fk of fkDefs) {
                if (fk.references !== collectionName) continue;
                const fkCol = config.collections[fkCollName];
                if (!fkCol) continue;
                const referencingDocs = await fkCol.find({
                  selector: { [fk.field]: mapping.tempId }
                }).exec();
                for (const doc of referencingDocs) {
                  await fkCol.upsert({
                    ...doc,
                    [fk.field]: mapping.realId
                  });
                }
              }
            }
          }
          config.onRemap?.(collectionName, mapping);
        }
      };
      remapChain = remapChain.then(run, run);
      return remapChain;
    },
    resolve(tempId) {
      return mappings.get(tempId);
    },
    getMappings() {
      return mappings;
    },
    isTempId(id) {
      return id.startsWith(TEMP_PREFIX);
    },
    generateTempId() {
      if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return `${TEMP_PREFIX}${crypto.randomUUID()}`;
      }
      if (typeof crypto !== "undefined" && crypto.getRandomValues) {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
        return `${TEMP_PREFIX}${hex}`;
      }
      return `${TEMP_PREFIX}${Date.now()}_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
    }
  };
}

// src/ordered-sync.ts
function createOrderedSync(config) {
  const {
    order,
    collections,
    pushHandlers,
    foreignKeys = {},
    primaryKey = "id",
    tempPrefix = "_temp_"
  } = config;
  const idMap = /* @__PURE__ */ new Map();
  function isTempId(id) {
    return typeof id === "string" && id.startsWith(tempPrefix);
  }
  function remapForeignKeys(doc, collectionName) {
    const fks = foreignKeys[collectionName];
    if (!fks) return doc;
    const remapped = { ...doc };
    for (const fk of fks) {
      const value = remapped[fk.field];
      if (value && isTempId(value) && idMap.has(value)) {
        remapped[fk.field] = idMap.get(value);
      }
    }
    return remapped;
  }
  async function pushCollection(name) {
    const col = collections[name];
    const handler = pushHandlers[name];
    if (!col || !handler) return 0;
    const allDocs = await col.find().exec();
    const pendingDocs = allDocs.filter((doc) => {
      if (isTempId(doc[primaryKey])) return true;
      return false;
    });
    if (pendingDocs.length === 0) return 0;
    const changes = pendingDocs.map((doc) => ({
      operation: "insert",
      doc: remapForeignKeys(doc, name)
    }));
    const serverDocs = await handler(changes);
    let remapped = 0;
    if (Array.isArray(serverDocs)) {
      for (let i = 0; i < Math.min(pendingDocs.length, serverDocs.length); i++) {
        const tempId = pendingDocs[i][primaryKey];
        const serverDoc = serverDocs[i];
        const realId = serverDoc[primaryKey];
        if (tempId !== realId && isTempId(tempId)) {
          idMap.set(tempId, realId);
          await col.delete(tempId);
          await col.upsert(serverDoc);
          remapped++;
        }
      }
    }
    config.onCollectionSynced?.(name, changes.length, remapped);
    return changes.length;
  }
  return {
    async pushAll() {
      const pushed = {};
      const errors = {};
      for (const name of order) {
        try {
          pushed[name] = await pushCollection(name);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          errors[name] = msg;
          config.onError?.(name, err instanceof Error ? err : new Error(msg));
          break;
        }
      }
      return { pushed, errors };
    },
    pushCollection,
    get idMappings() {
      return idMap;
    }
  };
}

// src/service.ts
function createService(initialState) {
  let current = initialState;
  const state$ = new Observable();
  state$.next(current);
  return {
    get() {
      return current;
    },
    set(state) {
      current = state;
      state$.next(current);
    },
    update(fn) {
      current = fn(current);
      state$.next(current);
    },
    get state$() {
      return state$;
    },
    subscribe(fn) {
      return state$.subscribe(fn);
    },
    reset() {
      current = initialState;
      state$.next(current);
    },
    dispose() {
      state$.dispose();
    }
  };
}

// src/cache-through.ts
function findCacheMapping(url, mapping) {
  let pathname;
  try {
    pathname = url.startsWith("http") ? new URL(url).pathname : url.split("?")[0];
  } catch {
    return null;
  }
  const patterns = Object.entries(mapping).sort((a, b) => b[0].length - a[0].length);
  for (const [pattern, config] of patterns) {
    if (pathname === pattern || pathname.startsWith(pattern + "/") || pathname.startsWith(pattern + "?")) {
      return config;
    }
    if (pathname.startsWith(pattern)) {
      const next = pathname[pattern.length];
      if (!next || next === "/" || next === "?") return config;
    }
  }
  return null;
}
function extractCacheItems(data, dataKey, primaryKey) {
  if (!data || typeof data !== "object") return [];
  const obj = data;
  if (dataKey) {
    const nested = obj[dataKey];
    if (Array.isArray(nested)) return nested;
    if (nested && typeof nested === "object" && primaryKey in nested) return [nested];
    return [];
  }
  if (Array.isArray(data)) return data;
  if (Array.isArray(obj.data)) return obj.data;
  if (Array.isArray(obj.results)) return obj.results;
  if (Array.isArray(obj.items)) return obj.items;
  if (primaryKey in obj) return [data];
  return [];
}
function createCachingInterceptor(mapping) {
  return async (response) => {
    const url = response.config?.url ?? "";
    const method = (response.config?.method ?? "GET").toUpperCase();
    const config = findCacheMapping(url, mapping);
    if (!config) return response;
    if (config.getOnly !== false && method !== "GET") return response;
    try {
      const items = extractCacheItems(response.data, config.dataKey, config.collection.primaryKey);
      for (const item of items) {
        if (item && typeof item === "object" && item[config.collection.primaryKey]) {
          await config.collection.upsert(item);
        }
      }
    } catch {
    }
    return response;
  };
}
function createOfflineFallbackInterceptor(mapping) {
  return async (error) => {
    if (!error.isNetworkError && !error.isTimeout) return error;
    const method = (error.config?.method ?? "GET").toUpperCase();
    if (method !== "GET") return error;
    const url = error.config?.url ?? "";
    const config = findCacheMapping(url, mapping);
    if (!config) return error;
    try {
      const segments = url.split("/");
      const lastSegment = segments[segments.length - 1];
      const isItemRequest = lastSegment && !lastSegment.includes("?") && lastSegment !== "" && !Object.keys(mapping).some((p) => url.endsWith(p));
      let data;
      if (isItemRequest) {
        const doc = await config.collection.findOne(lastSegment);
        if (!doc) return error;
        data = doc;
      } else {
        const docs = await config.collection.find().exec();
        if (docs.length === 0) return error;
        data = config.dataKey ? { [config.dataKey]: docs } : docs;
      }
      return {
        data,
        status: 200,
        statusText: "OK (offline cache)",
        headers: new Headers({ "x-drystuff-cache": "true" }),
        config: error.config,
        duration: 0
      };
    } catch {
      return error;
    }
  };
}
async function seedFromResponse(collection, responseData, dataKey) {
  const items = extractCacheItems(responseData, dataKey, collection.primaryKey);
  let count = 0;
  for (const item of items) {
    if (item && typeof item === "object" && item[collection.primaryKey]) {
      await collection.upsert(item);
      count++;
    }
  }
  return count;
}

// src/relationships.ts
var RelationshipManager = class {
  constructor() {
    this._relations = /* @__PURE__ */ new Map();
    this._collections = /* @__PURE__ */ new Map();
  }
  /** Register a collection's relationships */
  register(collectionName, relationships, collectionRef) {
    this._relations.set(collectionName, new Map(Object.entries(relationships)));
    this._collections.set(collectionName, collectionRef);
  }
  /**
   * Handle a delete event — enforce onDelete actions.
   * Called when a document is deleted from any collection.
   */
  async onDelete(collectionName, deletedDoc) {
    const pk = this._collections.get(collectionName)?.primaryKey;
    if (!pk) return;
    const deletedId = deletedDoc[pk];
    for (const [refCollName, relations] of this._relations) {
      for (const [, rel] of relations) {
        if (rel.collection !== collectionName) continue;
        const refCol = this._collections.get(refCollName);
        if (!refCol) continue;
        const referencingDocs = await refCol.findByField(rel.foreignKey, deletedId);
        switch (rel.onDelete ?? "no-action") {
          case "cascade":
            for (const doc of referencingDocs) {
              const key = String(doc[refCol.primaryKey]);
              await refCol.deleteDoc(key);
            }
            break;
          case "set-null":
            for (const doc of referencingDocs) {
              await refCol.updateDoc({ ...doc, [rel.foreignKey]: null });
            }
            break;
          case "restrict":
            if (referencingDocs.length > 0) {
              throw new Error(
                `Cannot delete ${collectionName} doc: ${referencingDocs.length} ${refCollName} doc(s) still reference it via ${rel.foreignKey}`
              );
            }
            break;
        }
      }
    }
  }
  /**
   * Populate a document's relationships — replace FK values with full docs.
   */
  async populate(collectionName, doc, fields) {
    const relations = this._relations.get(collectionName);
    if (!relations) return doc;
    const populated = { ...doc };
    for (const field of fields) {
      const rel = relations.get(field);
      if (!rel) continue;
      const targetCol = this._collections.get(rel.collection);
      if (!targetCol) continue;
      const fkValue = populated[rel.foreignKey];
      if (fkValue !== null && fkValue !== void 0) {
        const targetDocs = await targetCol.findByField(targetCol.primaryKey, fkValue);
        populated[field] = targetDocs[0] ?? null;
      }
    }
    return populated;
  }
  /**
   * Populate an array of documents — batched for efficiency.
   * Collects all FK values first, does one lookup per referenced collection.
   */
  async populateMany(collectionName, docs, fields) {
    if (docs.length === 0) return docs;
    const relations = this._relations.get(collectionName);
    if (!relations) return docs;
    const fkValues = /* @__PURE__ */ new Map();
    for (const field of fields) {
      const rel = relations.get(field);
      if (!rel) continue;
      const values = /* @__PURE__ */ new Set();
      for (const doc of docs) {
        const v = doc[rel.foreignKey];
        if (v !== null && v !== void 0) values.add(v);
      }
      fkValues.set(field, values);
    }
    const resolved = /* @__PURE__ */ new Map();
    for (const field of fields) {
      const rel = relations.get(field);
      if (!rel) continue;
      const values = fkValues.get(field);
      if (!values || values.size === 0) continue;
      const targetCol = this._collections.get(rel.collection);
      if (!targetCol) continue;
      const docMap = /* @__PURE__ */ new Map();
      for (const v of values) {
        const targetDocs = await targetCol.findByField(targetCol.primaryKey, v);
        if (targetDocs[0]) docMap.set(v, targetDocs[0]);
      }
      resolved.set(field, docMap);
    }
    return docs.map((doc) => {
      const populated = { ...doc };
      for (const field of fields) {
        const rel = relations.get(field);
        if (!rel) continue;
        const docMap = resolved.get(field);
        if (!docMap) continue;
        const fkValue = populated[rel.foreignKey];
        populated[field] = docMap.get(fkValue) ?? null;
      }
      return populated;
    });
  }
};

// src/computed.ts
function applyComputed(doc, computed) {
  const result = { ...doc };
  for (const [name, fn] of Object.entries(computed)) {
    result[name] = fn(doc);
  }
  return result;
}
function applyComputedArray(docs, computed) {
  return docs.map((doc) => applyComputed(doc, computed));
}
function createDerivedCollection(source, config) {
  const obs = new Observable();
  let disposed = false;
  const refresh = async () => {
    if (disposed) return;
    const all = await source.find().exec();
    let filtered = all.filter(config.filter);
    if (config.transform) filtered = filtered.map(config.transform);
    obs.next(filtered);
  };
  refresh();
  const unsub = source.changes$.subscribe(() => {
    refresh();
  });
  return {
    name: config.name,
    async getAll() {
      const all = await source.find().exec();
      let filtered = all.filter(config.filter);
      if (config.transform) filtered = filtered.map(config.transform);
      return filtered;
    },
    $: obs,
    async count() {
      const docs = await this.getAll();
      return docs.length;
    },
    dispose() {
      disposed = true;
      unsub();
      obs.dispose();
    }
  };
}

// src/transaction.ts
async function transaction(db, fn) {
  const pending = /* @__PURE__ */ new Map();
  const getOrCreate = (name) => {
    if (!pending.has(name)) {
      pending.set(name, { collection: name, operation: {} });
    }
    return pending.get(name);
  };
  const txn = {
    collection(name) {
      const col = db.collection(name);
      if (!col) {
        throw new DryError("COLLECTION_CLOSED", `Collection "${name}" not found`, { collection: name });
      }
      return {
        async findOne(key) {
          return col.findOne(key);
        },
        find(query) {
          return col.find(query);
        },
        insert(doc) {
          const pw = getOrCreate(name);
          if (!pw.operation.insert) pw.operation.insert = [];
          pw.operation.insert.push(doc);
        },
        update(doc) {
          const pw = getOrCreate(name);
          if (!pw.operation.update) pw.operation.update = [];
          pw.operation.update.push(doc);
        },
        delete(key) {
          const pw = getOrCreate(name);
          if (!pw.operation.delete) pw.operation.delete = [];
          pw.operation.delete.push(key);
        }
      };
    }
  };
  await fn(txn);
  const errors = [];
  const committed = [];
  for (const [name, pw] of pending) {
    const col = db.collection(name);
    if (!col) continue;
    try {
      await col.storage.write(pw.operation);
      const now = Date.now();
      if (pw.operation.insert) {
        for (const doc of pw.operation.insert) {
          col.changes$.next({ operation: "insert", doc, timestamp: now });
        }
      }
      if (pw.operation.update) {
        for (const doc of pw.operation.update) {
          col.changes$.next({ operation: "update", doc, timestamp: now });
        }
      }
      committed.push(name);
    } catch (err) {
      errors.push(err instanceof Error ? err : new Error(String(err)));
    }
  }
  if (errors.length > 0) {
    const first = errors[0];
    throw new DryError(
      "TRANSACTION_FAILED",
      `Transaction failed: ${errors.length} of ${pending.size} collection(s) errored`,
      {
        originalError: first,
        suggestions: [
          "Check that all documents are valid before committing",
          `Committed collections: ${committed.length}, failed: ${errors.length}`
        ]
      }
    );
  }
}

// src/replication.ts
function defaultConflictHandler(local, remote) {
  const localTime = local["_updatedAt"] ?? 0;
  const remoteTime = remote["_updatedAt"] ?? 0;
  if (remoteTime === localTime) {
    const localId = JSON.stringify(local);
    const remoteId = JSON.stringify(remote);
    return { winner: remoteId >= localId ? remote : local };
  }
  return { winner: remoteTime > localTime ? remote : local };
}
function replicateCollection(options) {
  const {
    collection,
    batchSize = 50,
    retryDelay = 5e3,
    maxRetries: _maxRetries = 5,
    conflictHandler = defaultConflictHandler,
    circuitBreakerTimeout = 6e4,
    circuitBreakerThreshold = 5,
    checkpointStorage
  } = options;
  let active = false;
  let disposed = false;
  let pullInterval = null;
  let pushUnsub = null;
  let streamUnsub = null;
  let lastCheckpoint = null;
  let pushQueue = [];
  let pushInFlight = false;
  let consecutiveFailures = 0;
  let retryTimer = null;
  let circuitOpen = false;
  let circuitTimer = null;
  const tombstones = /* @__PURE__ */ new Map();
  const events$ = new Observable();
  function emit(event) {
    if (!disposed) events$.next(event);
  }
  function getPk(doc) {
    return String(doc[collection.primaryKey]);
  }
  async function saveCheckpoint(cp) {
    lastCheckpoint = cp;
    if (checkpointStorage) {
      try {
        await checkpointStorage.save(options.identifier, cp);
      } catch (err) {
        console.error("[DryStore replication] Failed to save checkpoint:", err);
      }
    }
  }
  async function loadCheckpoint() {
    if (checkpointStorage) {
      try {
        lastCheckpoint = await checkpointStorage.load(options.identifier);
      } catch (err) {
        console.error("[DryStore replication] Failed to load checkpoint:", err);
      }
    }
  }
  function tripCircuitBreaker() {
    if (circuitBreakerTimeout <= 0) return;
    if (circuitOpen) return;
    circuitOpen = true;
    emit({ type: "circuit-open", timestamp: Date.now() });
    circuitTimer = setTimeout(() => {
      circuitOpen = false;
      consecutiveFailures = 0;
      circuitTimer = null;
      emit({ type: "circuit-close", timestamp: Date.now() });
      if (pushQueue.length > 0 && active) flushPushQueue();
    }, circuitBreakerTimeout);
  }
  function onSuccess() {
    consecutiveFailures = 0;
    if (circuitOpen) {
      circuitOpen = false;
      if (circuitTimer) {
        clearTimeout(circuitTimer);
        circuitTimer = null;
      }
      emit({ type: "circuit-close", timestamp: Date.now() });
    }
  }
  function onFailure(err, context, affectedDocs) {
    consecutiveFailures++;
    const message = err instanceof Error ? err.message : err == null ? "unknown failure (null/undefined)" : typeof err === "string" ? err : (() => {
      try {
        return JSON.stringify(err);
      } catch {
        return String(err);
      }
    })();
    emit({ type: "error", error: new Error(`[${context}] ${message}`), docs: affectedDocs, timestamp: Date.now() });
    if (consecutiveFailures >= circuitBreakerThreshold) {
      tripCircuitBreaker();
    }
  }
  async function applyRemoteDocs(docs, deletedIds) {
    try {
      return await applyRemoteDocsInner(docs, deletedIds);
    } catch (err) {
      if (err == null) {
        throw new Error(
          `applyRemoteDocs failed with null/undefined rejection (${docs.length} docs, ${deletedIds?.length ?? 0} deletes). Likely cause: storage adapter rejected without setting an error.`
        );
      }
      throw err;
    }
  }
  async function applyRemoteDocsInner(docs, deletedIds) {
    if (deletedIds && deletedIds.length > 0) {
      for (const id of deletedIds) {
        await collection.delete(id);
        tombstones.set(id, { id, deletedAt: Date.now() });
      }
    }
    for (const remoteDoc of docs) {
      const id = getPk(remoteDoc);
      const localDoc = await collection.find({
        selector: { [collection.primaryKey]: id }
      }).exec().then((r) => r[0]);
      if (localDoc) {
        const { winner } = conflictHandler(localDoc, remoteDoc);
        const winnerId = getPk(winner);
        const resolution = winnerId === getPk(localDoc) && JSON.stringify(winner) === JSON.stringify(localDoc) ? "local" : winnerId === getPk(remoteDoc) && JSON.stringify(winner) === JSON.stringify(remoteDoc) ? "remote" : "merge";
        if (JSON.stringify(localDoc) !== JSON.stringify(remoteDoc)) {
          emit({
            type: "conflict",
            conflict: { docId: id, localDoc, remoteDoc, winner, resolution },
            timestamp: Date.now()
          });
        }
        await collection.upsert(winner);
      } else {
        await collection.upsert(remoteDoc);
      }
    }
  }
  async function doPull() {
    if (!options.pull || circuitOpen) return;
    try {
      const result = await options.pull.handler(lastCheckpoint, batchSize);
      if (result.documents.length > 0 || result.deletedIds && result.deletedIds.length > 0) {
        await applyRemoteDocs(result.documents, result.deletedIds);
        emit({ type: "pull", docs: result.documents, timestamp: Date.now() });
      }
      if (result.checkpoint) {
        await saveCheckpoint(result.checkpoint);
      }
      onSuccess();
    } catch (err) {
      onFailure(err, "pull");
    }
  }
  async function flushPushQueue() {
    if (!options.push || pushQueue.length === 0 || pushInFlight || circuitOpen) return;
    pushInFlight = true;
    const batch = pushQueue.splice(0, batchSize);
    try {
      const resolved = options.push.beforePush ? await options.push.beforePush(batch) : batch;
      await options.push.handler(resolved);
      emit({ type: "push", docs: resolved.map((c) => c.doc), timestamp: Date.now() });
      onSuccess();
    } catch (err) {
      pushQueue.unshift(...batch);
      onFailure(err, "push", batch.map((c) => c.doc));
      if (!circuitOpen && consecutiveFailures < circuitBreakerThreshold) {
        const delay = retryDelay * Math.pow(2, Math.min(consecutiveFailures - 1, 6));
        retryTimer = setTimeout(() => {
          retryTimer = null;
          if (active && !disposed) flushPushQueue();
        }, delay);
      }
    } finally {
      pushInFlight = false;
    }
    if (pushQueue.length > 0 && consecutiveFailures === 0 && !circuitOpen) {
      await flushPushQueue();
    }
  }
  function onLocalChange(event) {
    const id = getPk(event.doc);
    if (event.operation === "delete") {
      tombstones.set(id, { id, deletedAt: event.timestamp });
    }
    pushQueue.push({
      operation: event.operation,
      doc: event.doc
    });
    flushPushQueue();
  }
  let applyChain = Promise.resolve();
  function startStream() {
    if (!options.pull?.stream) return;
    if (streamUnsub) {
      streamUnsub();
      streamUnsub = null;
    }
    streamUnsub = options.pull.stream(
      (docs, deletedIds) => {
        applyChain = applyChain.then(
          () => applyRemoteDocs(docs, deletedIds).then(
            () => emit({ type: "pull", docs, timestamp: Date.now() }),
            (err) => onFailure(err, "stream-apply")
          )
        );
      },
      (err) => {
        onFailure(err, "stream");
      }
    );
  }
  function clearTimers() {
    if (pullInterval) {
      clearInterval(pullInterval);
      pullInterval = null;
    }
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    if (circuitTimer) {
      clearTimeout(circuitTimer);
      circuitTimer = null;
    }
  }
  function stopListeners() {
    if (pushUnsub) {
      pushUnsub();
      pushUnsub = null;
    }
    if (streamUnsub) {
      streamUnsub();
      streamUnsub = null;
    }
  }
  const state = {
    get active() {
      return active;
    },
    get circuitOpen() {
      return circuitOpen;
    },
    get pendingPushCount() {
      return pushQueue.length;
    },
    get tombstones() {
      return tombstones;
    },
    events$,
    async start() {
      if (active || disposed) return;
      active = true;
      await loadCheckpoint();
      emit({ type: "active", timestamp: Date.now() });
      if (options.initialPull && options.pull) {
        doPull();
      }
      if (options.pull?.interval) {
        if (!options.initialPull) {
          doPull();
        }
        pullInterval = setInterval(doPull, options.pull.interval);
      }
      startStream();
      if (options.push) {
        pushUnsub = collection.changes$.subscribe(onLocalChange);
      }
    },
    stop() {
      if (!active) return;
      active = false;
      clearTimers();
      stopListeners();
      circuitOpen = false;
      emit({ type: "paused", timestamp: Date.now() });
    },
    cancel() {
      clearTimers();
      pushQueue = [];
      consecutiveFailures = 0;
      circuitOpen = false;
    },
    dispose() {
      state.stop();
      disposed = true;
      events$.dispose();
      tombstones.clear();
      pushQueue = [];
    },
    async pull() {
      await doPull();
    },
    async push() {
      await flushPushQueue();
    }
  };
  return state;
}

// src/attachments.ts
var ATTACHMENT_PREFIX = "att_";
function generateAttachmentId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `${ATTACHMENT_PREFIX}${crypto.randomUUID()}`;
  }
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return `${ATTACHMENT_PREFIX}${hex}`;
  }
  return `${ATTACHMENT_PREFIX}${Date.now()}_${Math.random().toString(36).slice(2)}`;
}
function createAttachmentStore(backend) {
  return {
    async put(blob, meta) {
      const id = meta?.id ?? generateAttachmentId();
      const record = {
        id,
        blob,
        mime: meta?.mime ?? blob.type ?? "application/octet-stream",
        status: "local",
        retries: 0,
        createdAt: Date.now()
      };
      await backend.set(record);
      return id;
    },
    get(id) {
      return backend.get(id);
    },
    async listPending() {
      const all = await backend.getAll();
      return all.filter((r) => r.status === "local");
    },
    async markUploaded(id, url) {
      const rec = await backend.get(id);
      if (!rec) return;
      await backend.set({ ...rec, status: "uploaded", url });
    },
    async recordFailure(id, error) {
      const rec = await backend.get(id);
      if (!rec) return;
      await backend.set({ ...rec, retries: rec.retries + 1, lastError: error });
    },
    delete(id) {
      return backend.delete(id);
    }
  };
}
function createMemoryAttachmentBackend() {
  const map = /* @__PURE__ */ new Map();
  return {
    async get(id) {
      return map.get(id);
    },
    async getAll() {
      return Array.from(map.values());
    },
    async set(record) {
      map.set(record.id, record);
    },
    async delete(id) {
      map.delete(id);
    }
  };
}
function attachmentRef(id) {
  return { _attachment: id, status: "local" };
}
function isAttachmentRef(value) {
  return typeof value === "object" && value !== null && typeof value._attachment === "string";
}
function createAttachmentSync(config) {
  const { store, upload } = config;
  return {
    async beforePush(changes) {
      const inFlight = /* @__PURE__ */ new Map();
      function resolveId(id) {
        let p = inFlight.get(id);
        if (!p) {
          p = (async () => {
            const rec = await store.get(id);
            if (!rec) throw new Error(`Attachment "${id}" not found in store`);
            if (rec.status === "uploaded" && rec.url) return rec.url;
            try {
              const { url } = await upload(rec);
              await store.markUploaded(id, url);
              return url;
            } catch (err) {
              await store.recordFailure(id, err instanceof Error ? err.message : String(err));
              throw err;
            }
          })();
          inFlight.set(id, p);
        }
        return p;
      }
      async function resolveValue(value) {
        if (isAttachmentRef(value)) {
          const url = await resolveId(value._attachment);
          return { ...value, status: "uploaded", url };
        }
        if (Array.isArray(value)) {
          return Promise.all(value.map(resolveValue));
        }
        return value;
      }
      return Promise.all(
        changes.map(async (change) => {
          if (change.operation === "delete") return change;
          const entries = Object.entries(change.doc);
          const resolved = await Promise.all(
            entries.map(async ([k, v]) => [k, await resolveValue(v)])
          );
          return { ...change, doc: Object.fromEntries(resolved) };
        })
      );
    }
  };
}
function createIndexedDBAttachmentBackend(config = {}) {
  const dbName = config.dbName ?? "drystore-attachments";
  const storeName = config.storeName ?? "attachments";
  let dbPromise = null;
  function open() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: "id" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return dbPromise;
  }
  function tx(mode, run) {
    return open().then(
      (db) => new Promise((resolve, reject) => {
        const transaction2 = db.transaction(storeName, mode);
        const request = run(transaction2.objectStore(storeName));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      })
    );
  }
  return {
    async get(id) {
      const result = await tx("readonly", (s) => s.get(id));
      return result ?? void 0;
    },
    getAll() {
      return tx("readonly", (s) => s.getAll());
    },
    async set(record) {
      await tx("readwrite", (s) => s.put(record));
    },
    async delete(id) {
      await tx("readwrite", (s) => s.delete(id));
    }
  };
}

// src/websocket.ts
function splitChanges(changes, primaryKey) {
  const docs = [];
  const deletedIds = [];
  for (const raw of changes) {
    if (!raw || typeof raw !== "object") continue;
    const c = raw;
    const tag = c.operation ?? c.op;
    if (tag === "delete") {
      const explicit = c.id;
      const docId = c.doc && typeof c.doc === "object" ? c.doc[primaryKey] : void 0;
      const id = explicit ?? docId;
      if (id != null) {
        deletedIds.push(String(id));
      }
      continue;
    }
    if (c.doc) {
      docs.push(c.doc);
    }
  }
  return { docs, deletedIds };
}
function createWebSocketSync(config) {
  const {
    url,
    urlProvider,
    collection,
    conflictHandler,
    batchSize = 50,
    autoReconnect = true,
    maxReconnectAttempts = Infinity,
    reconnectDelay = 1e3,
    maxReconnectDelay = 3e4,
    heartbeatInterval = 3e4,
    heartbeatTimeout = 1e4,
    serialize = (msg) => JSON.stringify(msg),
    deserialize = (data) => JSON.parse(data),
    protocols,
    initialPull,
    readOnly = false
  } = config;
  if (!url && !urlProvider) {
    throw new Error(
      "createWebSocketSync: either `url` or `urlProvider` must be provided"
    );
  }
  let ws = null;
  let connectionStatus = "disconnected";
  let reconnectAttempts = 0;
  let reconnectTimer = null;
  let heartbeatTimer = null;
  let heartbeatTimeoutTimer = null;
  let intentionalClose = false;
  let lastCheckpoint = null;
  let connectGen = 0;
  let onStreamDocs = null;
  let onStreamError = null;
  const connectionStatus$ = new Observable();
  function setStatus(status) {
    connectionStatus = status;
    connectionStatus$.next(status);
  }
  function send(msg) {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(serialize(msg));
    }
  }
  function startHeartbeat() {
    if (heartbeatInterval <= 0) return;
    stopHeartbeat();
    heartbeatTimer = setInterval(() => {
      send({ type: "ping" });
      heartbeatTimeoutTimer = setTimeout(() => {
        ws?.close();
      }, heartbeatTimeout);
    }, heartbeatInterval);
  }
  function stopHeartbeat() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    if (heartbeatTimeoutTimer) {
      clearTimeout(heartbeatTimeoutTimer);
      heartbeatTimeoutTimer = null;
    }
  }
  function scheduleReconnect() {
    if (!autoReconnect || reconnectAttempts >= maxReconnectAttempts) {
      setStatus("disconnected");
      return;
    }
    setStatus("reconnecting");
    const delay = Math.min(
      reconnectDelay * Math.pow(2, reconnectAttempts),
      maxReconnectDelay
    );
    reconnectAttempts++;
    reconnectTimer = setTimeout(() => {
      doConnect();
    }, delay);
  }
  function cancelReconnect() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }
  async function resolveUrl() {
    if (urlProvider) {
      const resolved = await urlProvider();
      if (typeof resolved !== "string" || resolved.length === 0) {
        throw new Error("urlProvider returned an empty or non-string URL");
      }
      return resolved;
    }
    return url;
  }
  function doConnect() {
    if (ws) {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        return;
      }
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      ws = null;
    }
    setStatus("connecting");
    intentionalClose = false;
    const myGen = ++connectGen;
    resolveUrl().then((resolved) => {
      if (intentionalClose || myGen !== connectGen) return;
      openSocket(resolved);
    }).catch((err) => {
      if (myGen !== connectGen) return;
      onStreamError?.(err instanceof Error ? err : new Error(String(err)));
      if (!intentionalClose) {
        scheduleReconnect();
      }
    });
  }
  function openSocket(resolvedUrl) {
    const socket = new WebSocket(resolvedUrl, protocols);
    ws = socket;
    socket.onopen = () => {
      if (ws !== socket) return;
      setStatus("connected");
      reconnectAttempts = 0;
      startHeartbeat();
      if (initialPull) {
        initialPull(lastCheckpoint, batchSize).then((result) => {
          if (result.documents.length > 0 && onStreamDocs) {
            onStreamDocs(result.documents);
          }
          if (result.checkpoint) {
            lastCheckpoint = result.checkpoint;
          }
        }).catch((err) => {
          onStreamError?.(err instanceof Error ? err : new Error(String(err)));
        });
      }
    };
    socket.onmessage = (event) => {
      if (ws !== socket) return;
      let msg;
      try {
        msg = deserialize(typeof event.data === "string" ? event.data : String(event.data));
      } catch (err) {
        onStreamError?.(new Error(`Failed to deserialize WebSocket message: ${err}`));
        return;
      }
      switch (msg.type) {
        case "pong":
          if (heartbeatTimeoutTimer) {
            clearTimeout(heartbeatTimeoutTimer);
            heartbeatTimeoutTimer = null;
          }
          break;
        case "ping":
          send({ type: "pong" });
          break;
        case "push":
          if (msg.changes && onStreamDocs) {
            const { docs, deletedIds } = splitChanges(
              msg.changes,
              collection.primaryKey
            );
            if (docs.length > 0 || deletedIds.length > 0) {
              onStreamDocs(docs, deletedIds);
            }
          }
          if (msg.documents && onStreamDocs) {
            onStreamDocs(msg.documents);
          }
          if (msg.checkpoint) {
            lastCheckpoint = msg.checkpoint;
          }
          break;
        case "pull-response":
          if (msg.changes && onStreamDocs) {
            const { docs, deletedIds } = splitChanges(
              msg.changes,
              collection.primaryKey
            );
            if (docs.length > 0 || deletedIds.length > 0) {
              onStreamDocs(docs, deletedIds);
            }
          }
          if (msg.documents && onStreamDocs) {
            onStreamDocs(msg.documents);
          }
          if (msg.checkpoint) {
            lastCheckpoint = msg.checkpoint;
          }
          break;
        case "error":
          onStreamError?.(new Error(msg.error ?? "Unknown server error"));
          break;
      }
    };
    socket.onclose = () => {
      if (ws !== socket) return;
      stopHeartbeat();
      if (!intentionalClose) {
        scheduleReconnect();
      } else {
        setStatus("disconnected");
      }
    };
    socket.onerror = () => {
      if (ws !== socket) return;
      onStreamError?.(new Error(`WebSocket error connecting to ${resolvedUrl}`));
    };
  }
  const collectionName = collection.name ?? "collection";
  const replicationIdentifier = `ws-sync-${url ?? collectionName}`;
  const replication = replicateCollection({
    identifier: replicationIdentifier,
    collection,
    batchSize,
    conflictHandler,
    // Client→server push side. Omitted when `readOnly` is set so
    // remote-applied changes don't get echoed back to the server.
    ...readOnly ? {} : {
      push: {
        handler: async (changes) => {
          if (!ws || ws.readyState !== WebSocket.OPEN) {
            throw new Error("WebSocket not connected");
          }
          send({ type: "push", changes });
        }
      }
    },
    pull: {
      handler: async (checkpoint, bs) => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          send({ type: "pull-request", checkpoint, batchSize: bs });
        }
        return { documents: [], checkpoint };
      },
      stream: (onDocs, onError) => {
        onStreamDocs = onDocs;
        onStreamError = onError;
        return () => {
          onStreamDocs = null;
          onStreamError = null;
        };
      }
    }
  });
  const state = {
    get connectionStatus() {
      return connectionStatus;
    },
    connectionStatus$,
    replication,
    async connect() {
      doConnect();
      await replication.start();
    },
    disconnect() {
      intentionalClose = true;
      cancelReconnect();
      stopHeartbeat();
      replication.stop();
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.close();
        ws = null;
      }
      setStatus("disconnected");
    },
    async reconnect() {
      state.disconnect();
      reconnectAttempts = 0;
      await state.connect();
    },
    pull() {
      if (ws && ws.readyState === WebSocket.OPEN) {
        send({ type: "pull-request", version: 1, checkpoint: lastCheckpoint, batchSize });
      }
    }
  };
  return state;
}

// src/sync-state.ts
var SyncStateTracker = class {
  constructor(collection, primaryKey) {
    this._meta = /* @__PURE__ */ new Map();
    this._changeUnsub = null;
    this._eventUnsub = null;
    this._replication = null;
    /** Reactive sync status — emits on every state change */
    this.status$ = new Observable();
    this._collection = collection;
    this._primaryKey = primaryKey ?? collection.primaryKey;
    this._listenToChanges();
  }
  /** Attach to a replication instance to automatically track push/pull results */
  attach(replication) {
    this.detach();
    this._replication = replication;
    this._eventUnsub = replication.events$.subscribe((event) => {
      this._onReplicationEvent(event);
    });
  }
  /** Detach from replication */
  detach() {
    if (this._eventUnsub) {
      this._eventUnsub();
      this._eventUnsub = null;
    }
    this._replication = null;
  }
  // ─── Query methods ─────────────────────────────────────
  /** Get current sync status summary */
  getStatus() {
    let pending = 0;
    let failed = 0;
    let lastSyncAt = null;
    for (const meta of this._meta.values()) {
      if (meta.status === "pending") pending++;
      if (meta.status === "failed") failed++;
      if (meta.syncedAt !== null && (lastSyncAt === null || meta.syncedAt > lastSyncAt)) {
        lastSyncAt = meta.syncedAt;
      }
    }
    return {
      pending,
      failed,
      total: this._meta.size,
      isSynced: pending === 0 && failed === 0,
      lastSyncAt,
      isActive: this._replication?.active ?? false
    };
  }
  /** Check if a specific document is synced */
  isDocSynced(id) {
    const meta = this._meta.get(id);
    return meta ? meta.status === "synced" : true;
  }
  /** Get sync metadata for a specific document */
  getDocMeta(id) {
    return this._meta.get(id);
  }
  /** Get all documents that haven't been synced yet */
  async getUnsyncedDocs() {
    const pendingIds = this.getUnsyncedIds();
    if (pendingIds.length === 0) return [];
    const all = await this._collection.find({
      selector: { [this._primaryKey]: { $in: pendingIds } }
    }).exec();
    return all;
  }
  /** Get IDs of all unsynced documents (pending or failed) */
  getUnsyncedIds() {
    const ids = [];
    for (const [id, meta] of this._meta) {
      if (meta.status !== "synced") {
        ids.push(id);
      }
    }
    return ids;
  }
  /** Get IDs of failed documents only */
  getFailedIds() {
    const ids = [];
    for (const [id, meta] of this._meta) {
      if (meta.status === "failed") {
        ids.push(id);
      }
    }
    return ids;
  }
  /** Get all failed documents with their error info */
  getFailedDocs() {
    return Array.from(this._meta.values()).filter((m) => m.status === "failed");
  }
  /** Check if there are any unsynced changes */
  hasUnsyncedChanges() {
    for (const meta of this._meta.values()) {
      if (meta.status !== "synced") return true;
    }
    return false;
  }
  /** Get count of pending documents */
  get pendingCount() {
    let count = 0;
    for (const meta of this._meta.values()) {
      if (meta.status === "pending") count++;
    }
    return count;
  }
  /** Get count of failed documents */
  get failedCount() {
    let count = 0;
    for (const meta of this._meta.values()) {
      if (meta.status === "failed") count++;
    }
    return count;
  }
  /** Manually mark a doc as synced (useful for custom sync logic) */
  markSynced(id) {
    const meta = this._meta.get(id);
    if (meta) {
      meta.status = "synced";
      meta.syncedAt = Date.now();
      meta.retries = 0;
      meta.lastError = void 0;
      this._emitStatus();
    }
  }
  /** Manually mark a doc as failed */
  markFailed(id, error) {
    const meta = this._meta.get(id);
    if (meta) {
      meta.status = "failed";
      meta.retries++;
      meta.lastError = error;
      this._emitStatus();
    }
  }
  /** Reset a failed doc back to pending for retry */
  retryFailed(id) {
    if (id) {
      const meta = this._meta.get(id);
      if (meta && meta.status === "failed") {
        meta.status = "pending";
        meta.lastError = void 0;
      }
    } else {
      for (const meta of this._meta.values()) {
        if (meta.status === "failed") {
          meta.status = "pending";
          meta.lastError = void 0;
        }
      }
    }
    this._emitStatus();
  }
  /** Clear all tracking state */
  reset() {
    this._meta.clear();
    this._emitStatus();
  }
  /** Dispose all listeners */
  dispose() {
    this.detach();
    if (this._changeUnsub) {
      this._changeUnsub();
      this._changeUnsub = null;
    }
    this.status$.dispose();
  }
  // ─── Internal ──────────────────────────────────────────
  _listenToChanges() {
    this._changeUnsub = this._collection.changes$.subscribe((event) => {
      const id = String(event.doc[this._primaryKey]);
      if (event.operation === "delete") {
        this._meta.set(id, {
          id,
          status: "pending",
          localUpdatedAt: event.timestamp,
          syncedAt: null,
          retries: 0
        });
      } else {
        this._meta.set(id, {
          id,
          status: "pending",
          localUpdatedAt: event.timestamp,
          syncedAt: this._meta.get(id)?.syncedAt ?? null,
          retries: 0
        });
      }
      this._emitStatus();
    });
  }
  _onReplicationEvent(event) {
    if (event.type === "push" && event.docs) {
      for (const doc of event.docs) {
        const id = String(doc[this._primaryKey]);
        const meta = this._meta.get(id);
        if (meta) {
          meta.status = "synced";
          meta.syncedAt = event.timestamp;
          meta.retries = 0;
          meta.lastError = void 0;
        }
      }
      this._emitStatus();
    }
    if (event.type === "error" && event.docs) {
      for (const doc of event.docs) {
        const id = String(doc[this._primaryKey]);
        const meta = this._meta.get(id);
        if (meta && meta.status === "pending") {
          meta.retries++;
          meta.lastError = event.error?.message;
          if (meta.retries >= 3) {
            meta.status = "failed";
          }
        }
      }
      this._emitStatus();
    } else if (event.type === "error" && !event.docs) {
      this._emitStatus();
    }
    if (event.type === "pull" && event.docs) {
      for (const doc of event.docs) {
        const id = String(doc[this._primaryKey]);
        if (!this._meta.has(id) || this._meta.get(id).status === "synced") {
          this._meta.set(id, {
            id,
            status: "synced",
            localUpdatedAt: event.timestamp,
            syncedAt: event.timestamp,
            retries: 0
          });
        }
      }
      this._emitStatus();
    }
  }
  _emitStatus() {
    this.status$.next(this.getStatus());
  }
};

// src/index-engine.ts
var SortedIndex = class {
  constructor(field) {
    this._entries = [];
    this._field = field;
  }
  get field() {
    return this._field;
  }
  get size() {
    return this._entries.length;
  }
  /** Add a document to the index */
  add(doc, docId) {
    const key = doc[this._field];
    if (key === void 0 || key === null) return;
    const pos = this._bisectRight(key);
    this._entries.splice(pos, 0, { key, docId });
  }
  /** Remove a document from the index */
  remove(doc, docId) {
    const key = doc[this._field];
    if (key === void 0 || key === null) return;
    const pos = this._bisectLeft(key);
    for (let i = pos; i < this._entries.length && this._entries[i].key === key; i++) {
      if (this._entries[i].docId === docId) {
        this._entries.splice(i, 1);
        return;
      }
    }
  }
  /** Update: remove old, add new */
  update(oldDoc, newDoc, docId) {
    this.remove(oldDoc, docId);
    this.add(newDoc, docId);
  }
  /** Get all doc IDs where field === value. O(log n + k) */
  eq(value) {
    const ids = [];
    const pos = this._bisectLeft(value);
    for (let i = pos; i < this._entries.length && this._entries[i].key === value; i++) {
      ids.push(this._entries[i].docId);
    }
    return ids;
  }
  /** Get all doc IDs where field > value. O(log n + k) */
  gt(value) {
    const pos = this._bisectRight(value);
    return this._entries.slice(pos).map((e) => e.docId);
  }
  /** Get all doc IDs where field >= value. O(log n + k) */
  gte(value) {
    const pos = this._bisectLeft(value);
    return this._entries.slice(pos).map((e) => e.docId);
  }
  /** Get all doc IDs where field < value. O(log n + k) */
  lt(value) {
    const pos = this._bisectLeft(value);
    return this._entries.slice(0, pos).map((e) => e.docId);
  }
  /** Get all doc IDs where field <= value. O(log n + k) */
  lte(value) {
    const pos = this._bisectRight(value);
    return this._entries.slice(0, pos).map((e) => e.docId);
  }
  /** Get all doc IDs in sorted order (asc) */
  allAsc() {
    return this._entries.map((e) => e.docId);
  }
  /** Get all doc IDs in sorted order (desc) */
  allDesc() {
    return this._entries.map((e) => e.docId).reverse();
  }
  /** Get doc IDs in range [low, high] sorted */
  range(low, high) {
    const start = this._bisectLeft(low);
    const end = this._bisectRight(high);
    return this._entries.slice(start, end).map((e) => e.docId);
  }
  clear() {
    this._entries = [];
  }
  // Binary search: first position where entry.key >= target
  _bisectLeft(target) {
    let lo = 0;
    let hi = this._entries.length;
    while (lo < hi) {
      const mid = lo + hi >>> 1;
      if (this._compare(this._entries[mid].key, target) < 0) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }
  // Binary search: first position where entry.key > target
  _bisectRight(target) {
    let lo = 0;
    let hi = this._entries.length;
    while (lo < hi) {
      const mid = lo + hi >>> 1;
      if (this._compare(this._entries[mid].key, target) <= 0) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }
  _compare(a, b) {
    if (a === b) return 0;
    if (a === null || a === void 0) return -1;
    if (b === null || b === void 0) return 1;
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a) < String(b) ? -1 : 1;
  }
};
var HashIndex = class {
  constructor(field) {
    this._map = /* @__PURE__ */ new Map();
    this._field = field;
  }
  get field() {
    return this._field;
  }
  get size() {
    return this._map.size;
  }
  add(doc, docId) {
    const key = doc[this._field];
    if (key === void 0 || key === null) return;
    let set = this._map.get(key);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      this._map.set(key, set);
    }
    set.add(docId);
  }
  remove(doc, docId) {
    const key = doc[this._field];
    if (key === void 0 || key === null) return;
    const set = this._map.get(key);
    if (set) {
      set.delete(docId);
      if (set.size === 0) this._map.delete(key);
    }
  }
  update(oldDoc, newDoc, docId) {
    this.remove(oldDoc, docId);
    this.add(newDoc, docId);
  }
  /** O(1) equality lookup */
  eq(value) {
    return Array.from(this._map.get(value) ?? []);
  }
  /** O(k) lookup for $in operator */
  in(values) {
    const result = /* @__PURE__ */ new Set();
    for (const v of values) {
      const set = this._map.get(v);
      if (set) for (const id of set) result.add(id);
    }
    return Array.from(result);
  }
  clear() {
    this._map.clear();
  }
};
var CompositeIndex = class {
  constructor(field1, field2) {
    this._partitions = /* @__PURE__ */ new Map();
    this._field1 = field1;
    this._field2 = field2;
  }
  get fields() {
    return [this._field1, this._field2];
  }
  get partitionCount() {
    return this._partitions.size;
  }
  add(doc, docId) {
    const key1 = doc[this._field1];
    if (key1 === void 0 || key1 === null) return;
    let partition = this._partitions.get(key1);
    if (!partition) {
      partition = new SortedIndex(this._field2);
      this._partitions.set(key1, partition);
    }
    partition.add(doc, docId);
  }
  remove(doc, docId) {
    const key1 = doc[this._field1];
    if (key1 === void 0 || key1 === null) return;
    const partition = this._partitions.get(key1);
    if (partition) {
      partition.remove(doc, docId);
      if (partition.size === 0) this._partitions.delete(key1);
    }
  }
  update(oldDoc, newDoc, docId) {
    this.remove(oldDoc, docId);
    this.add(newDoc, docId);
  }
  /** Equality on field1, then range/equality on field2 */
  query(eq1, op2) {
    const partition = this._partitions.get(eq1);
    if (!partition) return [];
    if ("$eq" in op2) return partition.eq(op2.$eq);
    if ("$gte" in op2 && "$lte" in op2) return partition.range(op2.$gte, op2.$lte);
    if ("$gt" in op2 && "$lt" in op2) {
      const ids = partition.range(op2.$gt, op2.$lt);
      return ids;
    }
    if ("$gt" in op2) return partition.gt(op2.$gt);
    if ("$gte" in op2) return partition.gte(op2.$gte);
    if ("$lt" in op2) return partition.lt(op2.$lt);
    if ("$lte" in op2) return partition.lte(op2.$lte);
    return partition.allAsc();
  }
  /** Equality on field1, get all in sorted order */
  allForKey(key1, direction = "asc") {
    const partition = this._partitions.get(key1);
    if (!partition) return [];
    return direction === "desc" ? partition.allDesc() : partition.allAsc();
  }
  /** $in on field1, merge results */
  queryIn(values1, op2) {
    const result = [];
    for (const v of values1) {
      if (op2) {
        result.push(...this.query(v, op2));
      } else {
        const partition = this._partitions.get(v);
        if (partition) result.push(...partition.allAsc());
      }
    }
    return result;
  }
  clear() {
    this._partitions.clear();
  }
};
var IndexManager = class {
  constructor() {
    this._hashIndexes = /* @__PURE__ */ new Map();
    this._sortedIndexes = /* @__PURE__ */ new Map();
    this._compositeIndexes = [];
  }
  /** Create a hash index on a field */
  createHashIndex(field) {
    if (this._hashIndexes.has(field)) return this._hashIndexes.get(field);
    const index = new HashIndex(field);
    this._hashIndexes.set(field, index);
    return index;
  }
  /** Create a sorted index on a field */
  createSortedIndex(field) {
    if (this._sortedIndexes.has(field)) return this._sortedIndexes.get(field);
    const index = new SortedIndex(field);
    this._sortedIndexes.set(field, index);
    return index;
  }
  /** Create a composite index on two fields (equality on first, range on second) */
  createCompositeIndex(field1, field2) {
    const existing = this._compositeIndexes.find((c) => c.fields[0] === field1 && c.fields[1] === field2);
    if (existing) return existing;
    const index = new CompositeIndex(field1, field2);
    this._compositeIndexes.push(index);
    return index;
  }
  getHashIndex(field) {
    return this._hashIndexes.get(field);
  }
  getSortedIndex(field) {
    return this._sortedIndexes.get(field);
  }
  /** Find a composite index covering the given fields */
  getCompositeIndex(field1, field2) {
    return this._compositeIndexes.find((c) => c.fields[0] === field1 && c.fields[1] === field2);
  }
  /** Find any composite index where field1 matches */
  getCompositeIndexByFirstField(field1) {
    return this._compositeIndexes.find((c) => c.fields[0] === field1);
  }
  /** Notify all indexes of an insert */
  onInsert(doc, docId) {
    for (const idx of this._hashIndexes.values()) idx.add(doc, docId);
    for (const idx of this._sortedIndexes.values()) idx.add(doc, docId);
    for (const idx of this._compositeIndexes) idx.add(doc, docId);
  }
  /** Notify all indexes of an update */
  onUpdate(oldDoc, newDoc, docId) {
    for (const idx of this._hashIndexes.values()) idx.update(oldDoc, newDoc, docId);
    for (const idx of this._sortedIndexes.values()) idx.update(oldDoc, newDoc, docId);
    for (const idx of this._compositeIndexes) idx.update(oldDoc, newDoc, docId);
  }
  /** Notify all indexes of a delete */
  onDelete(doc, docId) {
    for (const idx of this._hashIndexes.values()) idx.remove(doc, docId);
    for (const idx of this._sortedIndexes.values()) idx.remove(doc, docId);
    for (const idx of this._compositeIndexes) idx.remove(doc, docId);
  }
  /** Build all indexes from a full dataset */
  rebuild(docs, _primaryKey) {
    for (const idx of this._hashIndexes.values()) idx.clear();
    for (const idx of this._sortedIndexes.values()) idx.clear();
    for (const idx of this._compositeIndexes) idx.clear();
    for (const [id, doc] of docs) {
      for (const idx of this._hashIndexes.values()) idx.add(doc, id);
      for (const idx of this._sortedIndexes.values()) idx.add(doc, id);
      for (const idx of this._compositeIndexes) idx.add(doc, id);
    }
  }
  clear() {
    for (const idx of this._hashIndexes.values()) idx.clear();
    for (const idx of this._sortedIndexes.values()) idx.clear();
    for (const idx of this._compositeIndexes) idx.clear();
  }
};

// src/migration.ts
function defineMigrations(map) {
  return map;
}
function migrateDoc(doc, fromVersion, toVersion, migrations) {
  if (fromVersion >= toVersion) return doc;
  let current = doc;
  for (let v = fromVersion + 1; v <= toVersion; v++) {
    const fn = migrations[v];
    if (!fn) {
      throw new Error(
        `Missing migration for version ${v}. Document is at version ${fromVersion}, target is ${toVersion}. Define migrations for every version increment.`
      );
    }
    current = fn(current);
  }
  return current;
}
var VERSION_FIELD = "_schemaVersion";
var MigratedStorage = class {
  constructor(inner, targetVersion, migrations, primaryKey) {
    this._inner = inner;
    this._targetVersion = targetVersion;
    this._migrations = migrations;
    this._primaryKey = primaryKey;
    this.name = `migrated(${inner.name})`;
  }
  async get(key) {
    const doc = await this._inner.get(key);
    return doc ? this._migrate(doc) : void 0;
  }
  async getMany(keys) {
    const docs = await this._inner.getMany(keys);
    return Promise.all(docs.map((d) => this._migrate(d)));
  }
  async getAll() {
    const docs = await this._inner.getAll();
    return Promise.all(docs.map((d) => this._migrate(d)));
  }
  async write(ops) {
    const stamped = {};
    if (ops.insert) {
      stamped.insert = ops.insert.map((d) => this._stamp(d));
    }
    if (ops.update) {
      stamped.update = ops.update.map((d) => this._stamp(d));
    }
    if (ops.delete) {
      stamped.delete = ops.delete;
    }
    return this._inner.write(stamped);
  }
  async find(query) {
    const docs = await this._inner.find(query);
    return Promise.all(docs.map((d) => this._migrate(d)));
  }
  async count(selector) {
    return this._inner.count(selector);
  }
  async close() {
    return this._inner.close();
  }
  /**
   * Eagerly migrate all documents in storage to the target version.
   * Call this on app startup if you want to migrate ahead of time.
   */
  async migrateAll() {
    const docs = await this._inner.getAll();
    let migrated = 0;
    for (const doc of docs) {
      const version = doc[VERSION_FIELD] ?? 0;
      if (version < this._targetVersion) {
        const updated = await this._migrate(doc);
        await this._inner.write({ update: [this._stamp(updated)] });
        migrated++;
      }
    }
    return migrated;
  }
  async _migrate(doc) {
    const version = doc[VERSION_FIELD] ?? 0;
    if (version >= this._targetVersion) return doc;
    const migrated = migrateDoc(doc, version, this._targetVersion, this._migrations);
    const stamped = this._stamp(migrated);
    try {
      await this._inner.write({ update: [stamped] });
    } catch {
    }
    return migrated;
  }
  _stamp(doc) {
    return { ...doc, [VERSION_FIELD]: this._targetVersion };
  }
};
function createMigratedStorage(inner, schema, migrations) {
  return new MigratedStorage(inner, schema.version, migrations, schema.primaryKey);
}

export { AuditLog, Collection, CompositeIndex, Database, DryError, HashIndex, IndexManager, MigratedStorage, Observable, RelationshipManager, RxQuery, SoftDeleteStorage, SortedIndex, SyncStateTracker, ValidationError, applyComputed, applyComputedArray, attachmentRef, combineLatest, createAttachmentStore, createAttachmentSync, createCachingInterceptor, createDatabase, createDerivedCollection, createIdRemapper, createIndexedDBAttachmentBackend, createMemoryAttachmentBackend, createMigratedStorage, createOfflineFallbackInterceptor, createOrderedSync, createService, createSoftDeleteStorage, createWebSocketSync, decodeCursor, defineMigrations, defineSchema, duplicateKey, encodeCursor, executeQuery, extractCacheItems, findCacheMapping, generateAttachmentId, isAttachmentRef, matchesSelector, mergeHooks, migrateDoc, notFound, paginatedQuery, replicateCollection, seedFromResponse, t, transaction, validateDoc, withSoftDelete, withTimestamps };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map